import { VoyageDialogPopupManager } from "../../local_modules/voyage-dialog-popup/src/voyage-dialog-popup-manager";
import { VesselPlanningModel } from "../models/vessel-planning-model";
import { VesselPositionModel } from "../models/vessel-position-model";
import { CoordinatesUtils } from "./coordinates-utils";
import { CreateDateLineAndGreatCircleFeatures, getLegTypeFromDist } from "./map-utils";
import { getDistance as gcDistance } from 'ol/sphere';
import { VoyageDialogPopupBodyWarning } from "../../local_modules/voyage-dialog-popup/src/voyage-dialog-popup-body-warning";
import { TerminalsModel } from "../models/terminals-model";
import { SvgIcons } from "../../local_modules/voyage-svg-icons/src/voyage-svg-icons";
import { GetFormattedLocalDate } from "./helpers";
import { WaypointTimestamps, PilotPointTimestamps, TimeseriesData, TimestampData, ChartNames } from "../../local_modules/voy-timeseries-chart/src/echartUtils";
import { VoyageSchedulesModel } from "../models/voyage-schedules-model";
import { ScheduleEntry } from "../models/types/voyage-schedules-model.types";
import { VoyagePlan } from "../models/vessel-planning-model";
import { VoyageRoute, RouteGeometry, VoyageRoutePointFeature, VoyageRouteLineFeature, RoutingJobOptions, RoutePassagePlanProperties, ShallowSounding } from "../models/voyage-routing-model";
import { VoyageRoutingModel } from "../models/voyage-routing-model";
import { Feature } from 'ol';
import { Geometry, LineString } from 'ol/geom';
import { lineString as turfLine, Feature as TurfFeature, LineString as turfLineString } from '@turf/helpers';
import lineIntersect from '@turf/line-intersect';
import { EtaDeviationWarningsTable } from "../../local_modules/voyage-dialog-popup/types/voyage-dialog-popup-eta-deviation-warning.types";
import { VoyageDialogPopupEtaDEviationPopupWarning } from "../../local_modules/voyage-dialog-popup/src/voyage-dialog-popup-eta-deviation-warning";
import { VesselRouteSelectionModel } from "../models/vessel-route-selection-model";
import { VesselPlansService } from "../services/vessel-plans-service";
import { BprAPIResponse } from "../models/types/voyage-best-practice-routes-impl.types";
import { ReasonCapturingInBackend } from "../../local_modules/vessel-own-route-reason-popup/src/vessels-own-route-reaon-popup-type";
import { VoyageImoModel } from "../models/voyage-imo-model";
import { AuthService } from '../services/auth/auth-service'
import { Coordinate } from "ol/coordinate";

export enum WPTypeDescription {
    RegularWP = "WP",
    ArrivalPilot = "arrival pilot point",
    DeparturePilot = "departure pilot point",
    ArrivalBerth = "arrival berth"
}

export const MINIMUM_PILOT_BERTH_GAP_MS = 60_000;
export const DEPARTURE_ETD_VALIDATION_ERROR = "ETD Pilot must be at least 1 minute later than ETD Berth. This applies even if Pilot and Berth waypoints are the same.";
export const ARRIVAL_ETA_VALIDATION_ERROR = "ETA Berth must be at least 1 minute later than ETA Pilot. This applies even if Pilot and Berth waypoints are the same.";

export function isAtLeastOneMinuteLater(earlierTime: number, laterTime: number): boolean {
    return laterTime - earlierTime >= MINIMUM_PILOT_BERTH_GAP_MS;
}

export function areDepartureEtdsValid(berthEtd: number, pilotEtd: number): boolean {
    return isAtLeastOneMinuteLater(berthEtd, pilotEtd);
}

export function areArrivalEtasValid(_plan: VoyagePlan | null | undefined, pilotEta: number, berthEta: number): boolean {
    return isAtLeastOneMinuteLater(pilotEta, berthEta);
}

export const ECDIS_FORMATS = ['csv', 'rtz','xml','json', 'route'];
const isVesselOnCloud = (<any>window)["appenv"].vesselOnCloud;

// Convert a uiType string to an index (returns -1 for no or unknown uiType strings)
export function uiTypeToIndex(uiType?: string): number
{
    let index = -1;
    if (uiType)
    {
        const regexMatch = /UserRoute([0-9]+)/.exec(uiType);
        if (regexMatch)
        {
            index = Number(regexMatch[1]);
        }
    }
    return index;
}

export async function markPassedWaypoints(route: VoyageRoute, ignoreIsPassedUpdate?: boolean, nextWPIndex?: number)
{
    if (nextWPIndex === undefined)
    {
        nextWPIndex = await getNextWaypointIndex(route.route_geometry, true);
    }

    const timeDelta = 1000*60*30; // Use existing isPassed if newer than 30 minutes to allow the user to correct the state
    if (ignoreIsPassedUpdate || !route.isPassedUpdate || new Date(route.isPassedUpdate).getTime() < Date.now() - timeDelta)
    {
        // Mark passed waypoints
        for (let i = 0; i < nextWPIndex; i++)
        {
            if (route.route_geometry[i].geometry.type === "Point")
            {
                if (isPointWithinStoppageWindow(route.route_geometry, i))
                {
                    continue;
                }
                route.route_geometry[i].properties.isPassed = true;
            }
        }
    }
}

export async function markPassedAndAddCurrentPosition(route: VoyageRoute, ignoreIsPassedUpdate?: boolean, ignoreDeviation?: boolean)
{
    const nextWPIndex = await getNextWaypointIndex(route.route_geometry, ignoreDeviation);
    markPassedWaypoints(route, ignoreIsPassedUpdate, nextWPIndex);
    insertCurrentPositionWP(route.route_geometry, nextWPIndex);
    return route;
}

export async function getNextWaypointIndex(geometry: RouteGeometry, ignoreDeviation?: boolean): Promise<number>
{
    let nextWPIndex;
    const position = VesselPositionModel!.position;
    if (!position || position.latitude === null || position.longitude === null)
    {
        console.warn("Missing position info");
        throw new Error("Missing current position information");
    }

    // Note: Allowing 50 nm deviation from route
    const allowedDeviation = ignoreDeviation ? -1 : 92600;
    const result = await VoyageRoutingModel!.getNextWaypointIndex(allowedDeviation, [position.longitude, position.latitude], geometry);
    if (result.result !== "failure")
    {
        nextWPIndex = result.nextWPIndex;
    }
    else
    {
        console.warn("Failed to calculate next waypoint! Result: ", result);
        throw new Error("Deviation from planned route too big")
    }

    return nextWPIndex;
}

/**
 * Returns true if current time falls within any stoppage window (now > stoppage ETA and now < stoppage ETD).
 * Used to skip inserting the current-position navigational waypoint while the vessel is in a stoppage.
 */
export function isWithinStoppageWindow(geometry: RouteGeometry): boolean
{
    for (let i = 0; i < geometry.length; i++)
    {
        if (isPointWithinStoppageWindow(geometry, i))
        {
            return true;
        }
    }
    return false;
}

export function isPointWithinStoppageWindow(geometry: RouteGeometry, index: number): boolean
{
    const feature = geometry[index];
    if (!feature || feature.geometry.type !== "Point")
    {
        return false;
    }

    const pointFeature = feature as VoyageRoutePointFeature;
    if (pointFeature.properties.is_stoppage_point !== true)
    {
        return false;
    }

    let etaStr = pointFeature.properties.eta;
    let etdStr = pointFeature.properties.etd;

    if ((typeof etaStr !== "string" || !etaStr) && pointFeature.properties.stoppage_end === true)
    {
        const previousPointIndex = index - 2;
        if (previousPointIndex >= 0 && geometry[previousPointIndex].geometry.type === "Point")
        {
            const previousPoint = geometry[previousPointIndex] as VoyageRoutePointFeature;
            const previousEta = previousPoint.properties.eta;
            if (previousPoint.properties.is_stoppage_point === true && typeof previousEta === "string" && previousEta)
            {
                etaStr = previousEta;
            }
        }
    }

    if (typeof etdStr !== "string" || !etdStr)
    {
        const nextPointIndex = index + 2;
        if (nextPointIndex < geometry.length && geometry[nextPointIndex].geometry.type === "Point")
        {
            const nextPoint = geometry[nextPointIndex] as VoyageRoutePointFeature;
            const nextEtd = nextPoint.properties.etd;
            if (nextPoint.properties.stoppage_end === true && typeof nextEtd === "string" && nextEtd)
            {
                etdStr = nextEtd;
            }
        }
    }

    if (typeof etaStr !== "string" || !etaStr)
    {
        return false;
    }

    if (typeof etdStr !== "string" || !etdStr)
    {
        return false;
    }

    const stoppageETA = new Date(etaStr).getTime();
    const stoppageETD = new Date(etdStr).getTime();
    if (isNaN(stoppageETA) || isNaN(stoppageETD))
    {
        return false;
    }

    const now = Date.now();
    return now > stoppageETA && now < stoppageETD;
}

export function insertCurrentPositionWP(geometry: RouteGeometry, index?: number)
{
    const position = VesselPositionModel!.position;
    if (!position)
    {
        // No current position, bail out
        return;
    }
    if (isWithinStoppageWindow(geometry))
    {
        return;
    }

    let legType: "GreatCircle"|"Rhumbline"|undefined;
    let speed_maximum: number|undefined;
    let speed_minimum: number|undefined;

    if (!index)
    {
        index = geometry.length;
    }
    else
    {
        // Get leg type from leg that we are partitioning
        legType = geometry[index-1].properties.leg_type;

        // Carry over speed limits, if any
        speed_maximum = geometry[index-2].properties.speed_maximum;
        speed_minimum = geometry[index-2].properties.speed_minimum;

    }

    const coords: [number,number] = [position.longitude!, position.latitude!];

    // Check if the current position is already present
    for (const feature of geometry)
    {
        if (feature.geometry.type === "Point" &&
        feature.geometry.coordinates[0] === coords[0] && feature.geometry.coordinates[1] == coords[1])
        {
            // Clear isPassed if set (since we need to include this WP in the calculation)
            delete feature.properties.isPassed;
            return;
        }
    }

    const newFeatures: RouteGeometry = [];
    const time = new Date().toISOString();
    const newPoint: VoyageRoutePointFeature = {
        type: "Feature",
        geometry: { type: "Point", coordinates: coords },
        properties: {
            type: "Navigational",
            vesselPositionAt: time,
            timestamp: time,
            userInserted: false,
        }
    };
    if (speed_maximum !== undefined)
    {
        newPoint.properties.speed_maximum = speed_maximum;
    }
    if (speed_minimum !== undefined)
    {
        newPoint.properties.speed_minimum = speed_minimum;
    }
    newFeatures.push(newPoint);

    if (index < geometry.length)
    {
        newFeatures.push({
            type: "Feature",
            geometry: {
                type: "LineString",
                coordinates: [coords, <[number,number]>geometry[index].geometry.coordinates]
            },
            properties: {
                leg_type: legType || getLegTypeFromDist(coords, <[number,number]>geometry[index].geometry.coordinates)
            }
        });
    }
    
    // Need to modify the leg before and insert a new point and new leg
    if (index > 0)
    {
        for (let i = index-1; i >= 0; i--)
        {
            if (geometry[i].geometry.type === "LineString")
            {
                // Properties we keep:
                // - leg_type
                // - distance (recalculated based on new end point)
                // - propulsion_power/propulsion_power_lower/propulsion_power_upper (since it is constant along the leg)
                // - main_engine_rpm/main_engine_rpm_lower/main_engine_rpm_upper (since it is constant along the leg)
                // - speed_over_ground - but taken from the first informational waypoint (if any), since the value is an average otherwise
                // - speed_through_water - but taken from the first informational waypoint (if any), since the value is an average otherwise
                // The rest is discarded, since the leg is modified
                const leg = <VoyageRouteLineFeature>geometry[i];
                leg.geometry.coordinates[1] = coords;
                const legType = leg.properties.leg_type;
                const distance = legType === "Rhumbline" ? CoordinatesUtils.calcRhumblineDist(leg.geometry.coordinates[0], leg.geometry.coordinates[1])/1000 :
                                                           gcDistance(leg.geometry.coordinates[0], leg.geometry.coordinates[1])/1000;

                leg.properties = {
                    leg_type: legType,
                    distance: distance,
                    propulsion_power: leg.properties.propulsion_power,
                    propulsion_power_lower: leg.properties.propulsion_power_lower,
                    propulsion_power_upper: leg.properties.propulsion_power_upper,
                    main_engine_rpm: leg.properties.main_engine_rpm,
                    main_engine_rpm_lower: leg.properties.main_engine_rpm_lower,
                    main_engine_rpm_upper: leg.properties.main_engine_rpm_upper,
                    speed_over_ground: leg.properties.informationalWaypoints ? leg.properties.informationalWaypoints[0].properties.speed_over_ground : leg.properties.speed_over_ground,
                    speed_through_water: leg.properties.informationalWaypoints ? leg.properties.informationalWaypoints[0].properties.speed_through_water : leg.properties.speed_through_water,
                };
                break;
            }
        }
    }
    geometry.splice(index, 0, ...newFeatures);
}

export async function SaveHydraLog(hydraRequestId: string) {
    const routingServiceEnv = (<any>window)["appenv"].services.routeoptimiserapi;
    const apiBasePath = (<any>window)["appenv"].apiBasePath;
    const name = `${hydraRequestId}.zip`;
    
    if (isVesselOnCloud) {
        const { scope } = (<any>window)["appenv"];
        try {
            const response = await fetch(`${apiBasePath}${routingServiceEnv.baseurl}/v1/hydra-logs/${hydraRequestId}.zip`, {
                headers: {
                    'Authorization': `Bearer ${await AuthService.getAccessToken(scope)}`,
                    'consumer-key': (<any>window)["appenv"].consumerKey,
                }
            });
            
            if (response.ok) {
                const blob = await response.blob();
                const blobUrl = URL.createObjectURL(blob);
                const exportElement = document.createElement('a');
                exportElement.download = name;
                exportElement.href = blobUrl;
                exportElement.click();
                URL.revokeObjectURL(blobUrl);
                return;
            }
        } catch (error) {
            console.error('Auth download failed:', error);
            return;
        }
    }
    
    // Fallback to direct link
    const exportElement = document.createElement('a');
    exportElement.download = name;
    exportElement.href = `${routingServiceEnv.baseurl}/v1/hydra-logs/${hydraRequestId}.zip`;
    exportElement.click();
}

// Serializes an arbitrary job/result object to a JSON file and triggers a browser download.
export function downloadJobLog(job: unknown, filename: string) {
    const blob = new Blob([JSON.stringify(job, null, 2)], { type: 'application/json' });
    const blobUrl = URL.createObjectURL(blob);
    const exportElement = document.createElement('a');
    exportElement.download = filename;
    exportElement.href = blobUrl;
    exportElement.click();
    URL.revokeObjectURL(blobUrl);
}

// Recalculate routes for the given plan and save the result
// If noChanges are set, the recalculation is solely to get an updated calculation without any actual changes to the route or plan,
// otherwise it is assumed that something (non-passage plan related) has been changed in the plan that invalidates the current calculation(s)
// Note that it is assumed that the passage plan is unchanged in this function; If that is not the case, set plan.status before calling this function
export async function recalcRoutes(plan: VoyagePlan, waitForCalcJobs?: boolean, noChanges?: boolean, oldPlanPilotEta?: number): Promise<void>
{
    const wasPlanApproved = plan.status === "approved";
    const selectRouteIndex = plan.selectedRouteIndex;

    const oldPlan = await VesselPlanningModel!.getCurrentPlan(plan.scheduleId);

    if (!noChanges)
    {
        // Save plan before calculating so changes are not lost, BUT:
        // 1) Remove route_infos for the routes to signify they haven't been calculated yet
        // 2) For non-approved plans, reset selectedRouteIndex until the calculation finishes
        //    (since you cannot have a non-calculated route selected).
        // Approved plans should remain approved and keep their selected route during recalculation.
        if (!wasPlanApproved)
        {
            plan.selectedRouteIndex = -1;
            plan.status = "planning";
        }
        for (let i = 0; i < plan.routes.length; i++)
        {
            plan.routes[i].recalcNeeded = true;
            delete plan.routes[i].route_info;
            plan.routes[i].recalcNeeded = true;
        }
    
        await VesselPlanningModel!.setPlan(plan.scheduleId, plan, false);
    }

    const executingPlan = await VesselPlanningModel!.getExecutingPlan();
    const calcJobs: Promise<VoyageRoute>[] = [];

    if (executingPlan && executingPlan.scheduleId === plan.scheduleId && plan.etd < new Date().getTime())
    {
        // Executing plan - update route(s) from current position
        for (let i = 0; i < plan.routes.length; i++)
        {
            const route = plan.routes[i];
            try
            {
                // only add position as waypoint and mark passed if the plan's etd is in the past
                if(plan.etd < new Date().getTime()) {
                    await markPassedAndAddCurrentPosition(route);
                }
            }
            catch (err: any)
            {
                if (i === plan.selectedRouteIndex)
                {
                    VoyageDialogPopupManager.createDialog({
                        headerText: "Re-calculation error",
                        bodyText: 'Failed to add current position to route. Please check route by using "Edit route"',
                        isError: true,
                        errorDetails: err.message
                    });    
                }
                continue;
            }

            const options: RoutingJobOptions = {
                routeId: route.routeId,
                draftFore: plan.foreDraft,
                draftAft: plan.aftDraft,
                gmt: plan.gmt !== null ? plan.gmt : undefined,
                etd: Date.now(),
                eta: plan.eta,
                rollAngle: plan.rollAngle !== null ? plan.rollAngle : undefined,
                numberOfReefers: plan.numberOfReefers,
                routeType: "WeatherOptimized",
                uiRouteIndex: executingPlan.routes.findIndex(r => r.routeId === route.routeId),
                planScheduleId: plan.scheduleId,
                arrivalPilotEta: plan.arrivalPilotEta,
                departurePilotEtd: plan.departurePilotEtd,
                updatePlan: {
                    routeIndex: i,
                    autoApprove: wasPlanApproved,
                    selectRoute: i === selectRouteIndex,
                    selectedRouteIndex: selectRouteIndex
                },
                passagePlan: route.passagePlan,
                recalcParametersUpdate: true,
                weatherRoutingInfo: route?.weatherRoutingInfo,
                oldPlanArrivalPilotEta: oldPlanPilotEta ?? oldPlan?.arrivalPilotEta,
                solidGm: plan.solidGm !== null ? plan.solidGm : undefined,
            }

            if (route.route_info) {
                options.whaleZonesApplied = route.route_info!.whaleZonesApplied;
                options.disableWhaleZoneSpeedRestrictions = route.route_info!.disableWhaleZoneSpeedRestrictions;
            }

            calcJobs.push(VoyageRoutingModel!.postRoutingJobUsingRoute(route, options));
        }
    }
    else
    {
        for (let i = 0; i < plan.routes.length; i++)
        {
            const route = plan.routes[i];
            const options: RoutingJobOptions = {
                routeId: route.routeId,
                draftFore: plan.foreDraft,
                draftAft: plan.aftDraft,
                gmt: plan.gmt !== null ? plan.gmt : undefined,
                etd: plan.etd,
                eta: plan.eta,
                rollAngle: plan.rollAngle !== null ? plan.rollAngle : undefined,
                numberOfReefers: plan.numberOfReefers,
                routeType: "WeatherOptimized",
                uiRouteIndex: plan.routes.findIndex(r => r.routeId === route.routeId),
                planScheduleId: plan.scheduleId,
                arrivalPilotEta: plan.arrivalPilotEta,
                departurePilotEtd: plan.departurePilotEtd,
                updatePlan: {
                    routeIndex: i,
                    autoApprove: wasPlanApproved,
                    selectRoute: i === selectRouteIndex,
                    selectedRouteIndex: selectRouteIndex
                },
                passagePlan: route.passagePlan,
                recalcParametersUpdate: true,
                weatherRoutingInfo: route?.weatherRoutingInfo,
                oldPlanArrivalPilotEta: oldPlanPilotEta ?? oldPlan?.arrivalPilotEta,
                solidGm: plan.solidGm !== null ? plan.solidGm : undefined,
            }

            if (route.route_info) {
                options.whaleZonesApplied = route.route_info!.whaleZonesApplied;
                options.disableWhaleZoneSpeedRestrictions = route.route_info!.disableWhaleZoneSpeedRestrictions;
            }

            calcJobs.push(VoyageRoutingModel!.postRoutingJobUsingRoute(route, options));
        }
    }

    if (waitForCalcJobs)
    {
        await Promise.all(calcJobs);
    }
}

export function getUiRouteIndex(route: VoyageRoute): number|undefined
{
    if (route.uiType)
    {
        const regexMatch = /UserRoute([0-9]+)/.exec(route.uiType);
        if (regexMatch)
        {
            return Number(regexMatch[1]);
        }
    }
}

export function getPilotPointIndeces(route: VoyageRoute): number[]
{
    const ppIndeces: number[] = [];
    const geometry = route?.route_geometry;

    if (!geometry) {
        return ppIndeces;
    }

    for (let i = 0; i < geometry.length; i++)
    {
        if (!geometry[i].geometry || geometry[i].geometry.type !== "Point") continue;

        if (geometry[i].properties.pilotPoint)
        {
            ppIndeces.push(i);
        }
    }
    return ppIndeces;
}

// isStarRoute reports whether a route is a Star Route (a BPR, not an Open Ocean Route).
export const isStarRoute = (route: VoyageRoute): boolean =>
    route.routeCreatedBy === 'system' && route.uiType !== 'OOR' && !!route.bestPracticeRouteId &&
    !route.name?.startsWith('Open Ocean');

export async function createNotificationForChoosingOwnRoute(plan: VoyagePlan, scheduleId: string, prefetchedStarRoutes?: BprAPIResponse[] | null) { // there id after first update
    const optimizedBPRs = prefetchedStarRoutes ?? await VesselPlansService.getOptimizedBPRsByPortPair(plan.departureTerminalCode, plan.arrivalTerminalCode, scheduleId);
    const currentSelectedRouteIndex = plan.routes.findIndex(route => route.routeId === plan.currentSelectedRouteId);
    const createdBy = plan.routes[currentSelectedRouteIndex]?.routeCreatedBy
    let bprRankIDs = optimizedBPRs?.map(bpr => bpr.best_practice_route_id) ?? [];
    if (bprRankIDs.length === 0) {
        // Fall back to the star routes embedded in the plan, so a failed or empty
        // fetch (e.g. offline) cannot silently skip the reject-reason popup.
        bprRankIDs = plan.routes
            .filter(route => route.routeCreatedBy === 'system' && route.bestPracticeRouteId)
            .map(route => route.bestPracticeRouteId!);
    }
    // if it is a own route and if bpr route is available for the plan, show the popup
    if (createdBy !== 'system' && bprRankIDs.length > 0) {
        const planID = plan.id ?? 0;
        VesselRouteSelectionModel!.setPopupVisability(scheduleId, plan.departureTerminalCode, plan.arrivalTerminalCode, planID, plan.eta, plan.etd, bprRankIDs);
    }
}


/**
 * ==================================================================================
 * PRE-DEPARTURE NOTIFICATION: Star Route Adoption
 * ==================================================================================
 * 
 * Called during Pre-Departure approve & send flow to notify shore that vessel
 * has adopted a route with valid justification.
 * 
 * WHEN CALLED:
 * • During approveAndSend() in pre-departure state
 * • Only if: (isAligned OR isFuelEfficientRoute) = true
 * 
 * NOTIFICATION REASON (Priority Order):
 * 
 * 1. If isAligned = true:
 *    Reason: "I have aligned my own route with a Star Route"
 *    • Route geometry matches a star route within acceptable deviation limits
 *    • Determined during Save & Exit via backend API /bpr/adopted
 *    • Stored in route.bprAlignment.isAligned
 * 
 * 2. Else if isFuelEfficientRoute = true:
 *    Reason: "Vessel's own route is more fuel efficient"
 *    • Route uses less fuel than ALL available star routes
 *    • Calculated during approveBtnClicked() by comparing fuel consumption
 * 
 * PURPOSE:
 * • Justifies to shore operations why vessel chose own route over star route
 * • Provides evidence: geometric alignment OR fuel efficiency
 * • Prevents unnecessary queries about route deviation from recommendations
 * 
 * SCENARIO FLOW:
 * Save & Exit → Sets isAligned → approveBtnClicked → Calculates isFuelEfficient →
 * approveAndSend → THIS FUNCTION → Notification sent to shore
 * 
 * @param plan - Voyage plan being approved
 * @param scheduleId - Schedule ID
 * @param isAligned - Route geometrically aligned with star route
 * @param isFuelEfficientRoute - Route more fuel efficient than all star routes
 */
export async function createNotificationAdoptingStarRoute(plan: VoyagePlan, scheduleId: string, isAligned: boolean, isFuelEfficientRoute: boolean) {
    const optimizedBPRs = await VesselPlansService.getOptimizedBPRsByPortPair(plan.departureTerminalCode, plan.arrivalTerminalCode, scheduleId);
    let reason = "";
    if (isAligned) {
        reason = ReasonCapturingInBackend.IHaveAlignedMyOwnRouteWithAStarRoute;
    } else if (isFuelEfficientRoute) {
        reason = ReasonCapturingInBackend.VesselsOwnRouteIsMoreFuelEfficient;
    }
    const bprRankIDs = optimizedBPRs?.map(bpr => bpr.best_practice_route_id) ?? [];
    let planID = plan.id ?? 0;
    VesselRouteSelectionModel!.setNotificationData(scheduleId, plan.departureTerminalCode, plan.arrivalTerminalCode, planID, plan.eta, plan.etd, bprRankIDs);
    VesselRouteSelectionModel!.createVesselRouteSelectionNotification({ reason, comment: "" });
}


// Extract strings from an unknown input, that is either flat or nested
function extractStrings(input: unknown): string[] {
  return (
    JSON.stringify(input)
      .match(/"([^"]+)"/g)
      ?.map(s => s.slice(1, -1)) ?? []
  );
}   

export async function verifyRequestedETAs(route: VoyageRoute,plan:VoyagePlan, scheduleId?: string, routeIndex?: number) {
    if (!route || !route.route_geometry) {
        console.warn('Cannot verify ETAs: route or route_geometry is missing');
        return;
    }
    const routeName = route?.name ? route.name : `Own route`;
    if (scheduleId && routeIndex) {
        if (VoyageRoutingModel!.etaDeviationPopupIsShown(scheduleId, routeIndex)) {
            return;
        }
        VoyageRoutingModel!.setEtaDeviationPopupIsShown(scheduleId, routeIndex, "open");
    }
    const warnings = checkIfRequestedEtaWithin110PercentOfRequestedEta(route, plan);
    // An error occurred when extracting the warnings, likely due to invalid waypoint properties
    if (warnings instanceof Error) {        
        const warningElement = new VoyageDialogPopupBodyWarning;
        warningElement.icon = SvgIcons.exclamationTriangle;
        warningElement.warningSummary = "Due to technical error, the ETAs could not be verified. Please check the route and verify the ETAs manually.";
        warningElement.warningDescription = isVesselOnCloud ?  extractStrings(warnings).join("\n\n") : `${warnings.message}`;
        warningElement.descriptionBackgroundVisible = true;

        const popup = VoyageDialogPopupManager.createDialog({
            headerText: `ETAs could not be verified for route "${routeName}"`,
            bodyContent: warningElement,
            acceptButtonText: "OK"
        });
        if ((await popup.getResponse()) === "accept")
        {
            const routeIndex = route.uiType ? Number(route.uiType[route.uiType.length-1]) : undefined;
            if (scheduleId && routeIndex) {
                VoyageRoutingModel!.setEtaDeviationPopupIsShown(scheduleId, routeIndex, "close");
            }
        }
        return;
    }

    if (warnings.length > 0) {
        const bodyContent = new VoyageDialogPopupEtaDEviationPopupWarning();
        bodyContent.warnings = warnings;
        bodyContent.icon = SvgIcons.lightExclamationCircle;
        let arrivalAndDepartureCodes;
        arrivalAndDepartureCodes = `${TerminalsModel!.getCity(plan!.departureTerminalCode)} - ${TerminalsModel!.getCity(plan!.arrivalTerminalCode)}`;
        const headerText = warnings.length > 1 ?  `Requested ETAs cannot be met for ${routeName} - ${arrivalAndDepartureCodes}`: `Requested ETA cannot be met for ${routeName} - ${arrivalAndDepartureCodes}`;
        
        const popup = VoyageDialogPopupManager.createDialog({
            headerText,
            bodyContent,
        });
        if ((await popup.getResponse()) === "accept")
        {
            const routeIndex = route.uiType ? Number(route.uiType[route.uiType.length-1]) : undefined;
            if (scheduleId && routeIndex) {
                VoyageRoutingModel!.setEtaDeviationPopupIsShown(scheduleId, routeIndex, "close");
            }
        }
    }
}

export async function verifyWarningsFromCalculation(route: VoyageRoute, plan:VoyagePlan, scheduleId?: string, routeIndex?: number) {
    const routeName = route?.name ? route.name : `Own route`;
    if (scheduleId && routeIndex) {
        if (VoyageRoutingModel!.warningPopupIsShown(scheduleId, routeIndex)) {
            return;
        }
        VoyageRoutingModel!.setWarningPopupIsShown(scheduleId, routeIndex, "open");
    }
    const warnings = route.route_info?.warnings;
    // An error occurred when extracting the warnings, likely due to invalid waypoint properties
    if (Array.isArray(warnings) && warnings.length > 0 && typeof warnings[0] === 'string') {
        const warningElement = new VoyageDialogPopupBodyWarning;
        warningElement.icon = SvgIcons.exclamationTriangle;
        warningElement.warningSummary = "Warnings were found in the route calculation. Please check the details below.";
        warningElement.warningDescription = isVesselOnCloud ? extractStrings(warnings).join("\n\n") : warnings.map(w => typeof w === 'string' ? w : w.message).join("\n\n");
        warningElement.descriptionBackgroundVisible = true;

        const popup = VoyageDialogPopupManager.createDialog({
            headerText: `Warnings from calculation for route "${routeName}"`,
            bodyContent: warningElement,
            acceptButtonText: "OK"
        });
        if ((await popup.getResponse()) === "accept") {
            const routeIndex = route.uiType ? Number(route.uiType[route.uiType.length - 1]) : undefined;
            if (scheduleId && routeIndex) {
                VoyageRoutingModel!.setWarningPopupIsShown(scheduleId, routeIndex, "close");
            }
        }
        return;
    }
}

export async function extractEtaWarnings(route: VoyageRoute, scheduleId?: string): Promise<EtaDeviationWarningsTable[]|Error> {
    const warnings: EtaDeviationWarningsTable[] = [];
    const pilotIndeces = getPilotPointIndeces(route);
    const departurePilotIndex = pilotIndeces[0];
    const arrivalPilotIndex = pilotIndeces[pilotIndeces.length-1];

    let wpNumber = 0;
    
    for (let i = 0; i < route.route_geometry.length; i++) {
        // Eta deviation warnings are only shown up to the arrival pilot point
        if (i > arrivalPilotIndex) {
            break;
        }
        const feature = route.route_geometry[i];
        if (feature.geometry.type !== "Point") {
            continue;
        }
        const point = <VoyageRoutePointFeature>feature;
        if (point.properties.stoppage_end === true) {
            continue;
        }
        if (feature.properties.type === "Hard") {
            wpNumber++; // Only count hard WPs i.e. user inserted WPs
        }
        if (point.properties.isPassed || !point.properties.eta_deviation || !(point.properties.type === "Hard" || point.properties.type === "Navigational")) {
            continue;
        }
        
        const isArrivalPilot = i === arrivalPilotIndex;
        const isDeparturePilot = i === departurePilotIndex;
        let timezone = "UTC";
        let requestedEta = point.properties.eta;
        let actualEta = point.properties.timestamp;

        if ((!requestedEta || !actualEta)) {
            // OBS: Throwing an error with the intention of setting the route status to "failed" will
            // not work. Route status is set to "failed" if the route_info is missing, which only happens
            // when Hydra fails. So the logic in voyage-routing-model in _updateRoutesFromPlan function
            // needs to be changed to support this. So currently, the route status will be succesful even
            // if the requested ETAs cannot be met.
            // For now, just log the error with console.error and continue.
            requestedEta = requestedEta || point.properties.eta_deviation?.eta || "";
            console.error(`Invalid waypoint properties. Requested ETA: ${requestedEta}, actual ETA: ${actualEta}, WP type: ${point.properties.type}`);
            return new Error(
                `For waypoint ${wpNumber}, the system has calculated actual ETA: ${actualEta}, while the requested ETA is: ${requestedEta} according to schedule or waypoint's ETA. 
                Please check the route and verify the ETAs manually in the route editor as well in the schedule.`);
        }

        // convert to local time if WP is pilot
        if (scheduleId && (isArrivalPilot || isDeparturePilot)) {
            const localTimes = await _getPilotLocalTime(isArrivalPilot ? "arrival" : "departure", scheduleId, requestedEta, actualEta);
            requestedEta = localTimes.localRequestedEta;
            actualEta = localTimes.localActualEta;
            timezone = "LT";
        }

        const position = CoordinatesUtils.decimalToDegreesAndMinutes(point.geometry.coordinates);
        warnings.push({
            wpNumber: point.properties.type === "Hard" ? wpNumber : null, // only hard WPs have wp number
            RequestedEta: `${GetFormattedLocalDate(requestedEta)} ${timezone}`,
            ActualEta: `${GetFormattedLocalDate(actualEta)} ${timezone}`,
            Coordinates: `${position.lat}, ${position.lon}`,
            wpType: point.properties.type,
            isPilot: isArrivalPilot || isDeparturePilot,
        });
    }
    return warnings;
}

export function checkIfRequestedEtaWithin110PercentOfRequestedEta(
    route: VoyageRoute,
    plan: VoyagePlan
  ): EtaDeviationWarningsTable[] {
    const warnings: EtaDeviationWarningsTable[] = [];
    const pilotIndices = getPilotPointIndeces(route);
    const departurePilotIndex = pilotIndices[0];
    const arrivalPilotIndex = pilotIndices[pilotIndices.length - 1];
  
    const departurePilotEtd = plan.departurePilotEtd
      ? new Date(plan.departurePilotEtd).getTime()
      : 0;
  
    const now = Date.now();
    // Always use later of ETD or current time
    const startTime = Math.max(departurePilotEtd, now);
  
    let wpNumber = (departurePilotIndex / 2) + 1;

    // Start from the first hard waypoint after the departure pilot point and end at the arrival pilot point
    for (let i = departurePilotIndex + 1; i <= arrivalPilotIndex; i++) {
      const feature = route.route_geometry[i];
      if (feature.geometry.type !== "Point") continue;
  
      const point = <VoyageRoutePointFeature>feature;
      if (point.properties.stoppage_end === true) continue;
      if (point.properties.type !== "Hard" || point.properties.isPassed) continue;
      wpNumber++;
  
      const requestedEta = point.properties.eta;
      const actualEta = point.properties.timestamp;
      if (!requestedEta || !actualEta) continue;
  
      const requestedEtaDate = new Date(requestedEta);
      const actualEtaDate = new Date(actualEta);
  
      // Offsets from start time
      const requestedEtaDiff = requestedEtaDate.getTime() - startTime;
      const actualEtaDiff = actualEtaDate.getTime() - startTime;
  
      // Absolute percentage deviation
      const deviationPercent = (Math.abs(actualEtaDiff - requestedEtaDiff) / requestedEtaDiff) * 100;

      const requestedEtaDiffInHours = requestedEtaDiff / (1000 * 60 * 60);
      const actualEtaDiffInHours = actualEtaDiff / (1000 * 60 * 60);

      console.log(
        "actualEta", new Date(actualEta).toISOString(),
        "requestedEta", new Date(requestedEta).toISOString(),
        "startTime", new Date(startTime).toISOString(),
        "requestedEtaDiffInHours", requestedEtaDiffInHours,
        "actualEtaDiffInHours", actualEtaDiffInHours,
        "deviationPercent", deviationPercent,
        "wpNumber", wpNumber,
      )
  
      // Trigger if deviation exceeds ±1.1%
      if (deviationPercent > 1.1) {
        warnings.push({
          wpNumber,
          RequestedEta: `${GetFormattedLocalDate(requestedEta)} UTC`,
          ActualEta: `${GetFormattedLocalDate(actualEta)} UTC`,
          Coordinates: `${point.geometry.coordinates[0]}, ${point.geometry.coordinates[1]}`,
          wpType: point.properties.type as "Hard" | "Navigational",
          isPilot: point.properties.pilotPoint ?? false,
        });
      }
    }
  
    return warnings;
  }

// Under Keel Clearance, in meters, for a given depth/tide/draft/CATZOC combination.
export function calcUKC(minDepth: number, tide: number, draft: number, catZoc: string, ignoreCatZoc: boolean): number {
    if (ignoreCatZoc) {
        return minDepth + tide - draft;
    }

    const catZocValue = getCatZocUKCValue(catZoc, minDepth);
    if (!catZocValue) {
        return 0;
    }

    return minDepth + tide - draft - catZocValue;
}

export function getCatZocUKCValue(catZoc: string, minDepth: number): number|undefined {
    switch (catZoc) {
        case "A1":
            return 0.5 + 0.01*minDepth;
        case "A2":
        case "B":
            return 1.0 + 0.02*minDepth;

        case "C":
            return 2.0 + 0.05*minDepth;

        case "D":
        case "U":
            return (2.0 + 0.05*minDepth) * 1.1;
        default:
            return 0;
        }
}

// Walks route_geometry grouped into legs bounded by consecutive Hard waypoints (mirrors the
// grouping in voyage-passage-plan/data-extractor.ts), and flags every depth_source-bearing leg
// segment whose computed UKC is negative as a shallow sounding to warn about.
export function extractShallowSoundings(route: VoyageRoute, foreDraft: number, aftDraft: number): ShallowSounding[] {
    const draft = Math.max(foreDraft, aftDraft);
    if (!draft || !route.route_geometry?.length) {
        return [];
    }

    // Only warn about shallow soundings on the open-sea Pilot -> Pilot leg (mirrors the
    // Pilot -> Pilot bounds splitRouteGeometry computes for the UKC check) - soundings on the
    // berth -> pilot or pilot -> berth approach are outside the scope of this warning.
    const pilotPointIndeces: number[] = [];
    route.route_geometry.forEach((feature, index) => {
        if (feature.geometry.type === "Point" && feature.properties.pilotPoint) {
            pilotPointIndeces.push(index);
        }
    });

    // One pilot point means it is both arrival and departure pilot point (ie. there is no pilot -> pilot)
    let startIndex = -1;
    let endIndex = route.route_geometry.length;
    if (pilotPointIndeces.length >= 1) {
        startIndex = pilotPointIndeces[0];
        endIndex = pilotPointIndeces[pilotPointIndeces.length - 1];
    }
    if (startIndex === endIndex) {
        return [];
    }

    const hardWaypoints: VoyageRoutePointFeature[] = [];
    const legs: VoyageRouteLineFeature[] = [];
    route.route_geometry.forEach((feature, index) => {
        if (index < startIndex || index > endIndex) {
            return;
        }
        if (feature.geometry.type === "Point" && feature.properties.type === "Hard") {
            hardWaypoints.push(<VoyageRoutePointFeature>feature);
        } else if (feature.geometry.type === "LineString") {
            legs.push(<VoyageRouteLineFeature>feature);
        }
    });

    const filteredHardWaypoints = hardWaypoints.filter(
        wp => !wp.properties.is_stoppage_point || wp.properties.stoppage_end === true
    );

    const soundings: ShallowSounding[] = [];
    let legIdx = 0;
    for (let i = 0; i < filteredHardWaypoints.length - 1; i++) {
        const hardWaypoint = filteredHardWaypoints[i];
        const nextCoords = filteredHardWaypoints[i + 1].geometry.coordinates;
        const tide = hardWaypoint.properties.tide ?? hardWaypoint.properties.userInput?.tide ?? 0;

        let reachedNextHardWP = false;
        while (!reachedNextHardWP && legIdx < legs.length) {
            const leg = legs[legIdx];
            legIdx++;

            const depthSource = leg.properties.depth_source;
            if (depthSource && leg.properties.min_depth !== undefined) {
                const catzoc = leg.properties.catzoc;
                const ukc = calcUKC(leg.properties.min_depth, tide, draft, catzoc!, !catzoc);
                if (ukc <= 0) {
                    soundings.push({
                        coords: depthSource.coords,
                        depth: leg.properties.min_depth,
                        catzoc: catzoc!,
                        sourceType: depthSource.type,
                        ukc,
                    });
                }
            }

            const legCoords = leg.geometry.coordinates;
            const endCoords = legCoords[legCoords.length - 1];
            reachedNextHardWP = endCoords[0] === nextCoords[0] && endCoords[1] === nextCoords[1];
        }

        if (!reachedNextHardWP) {
            console.warn("extractShallowSoundings: ran out of leg segments before reaching the next Hard waypoint");
            break;
        }
    }

    return soundings;
}

async function _getPilotLocalTime(pilotType: "departure"|"arrival", scheduleId: string, requestedEta: string, actualEta: string): Promise<{ localRequestedEta: string; localActualEta: string; }> {
    const currentPlan = scheduleId ? await VesselPlanningModel!.getCurrentPlan(scheduleId) : null;
    let requestedEtaLocal = "";
    let actualEtaLocal = "";
    if (pilotType === "arrival") {
        requestedEtaLocal = await TerminalsModel!.getTerminalLocaltime(currentPlan!.arrivalTerminalCode, requestedEta);
        actualEtaLocal = await TerminalsModel!.getTerminalLocaltime(currentPlan!.arrivalTerminalCode, actualEta);
    }
    if (pilotType === "departure")
    {
        requestedEtaLocal = await TerminalsModel!.getTerminalLocaltime(currentPlan!.departureTerminalCode, requestedEta);
        actualEtaLocal = await TerminalsModel!.getTerminalLocaltime(currentPlan!.departureTerminalCode, actualEta);
    }
    return { localRequestedEta: requestedEtaLocal, localActualEta: actualEtaLocal };
}

export function getRouteDistance(route: VoyageRoute, startIdx: number, endIdx: number): number {
    let totalDist = 0;
    for (let i = startIdx; i <= endIdx; i++) {
        if (route.route_geometry[i].geometry.type === "Point") {
            continue;
        }
        totalDist += route.route_geometry[i].properties.distance;
    }
    return totalDist;
}

export function getRoutePropertyAverages(route: VoyageRoute, startIdx: number, endIdx: number)
    {
        const geometry = route.route_geometry;

        // Only LineString legs carry distance/engine properties. A leg is usable
        // for averaging only if it is a LineString with a finite, positive distance;
        // anything else (Points, or LineStrings with missing/NaN properties) would
        // poison the totals and yield NaN, so it is skipped.
        const isUsableLeg = (i: number) =>
            geometry[i].geometry.type === "LineString" &&
            Number.isFinite(geometry[i].properties.distance) &&
            geometry[i].properties.distance > 0;

        let totalDistance = 0;
        for (let i = startIdx; i < endIdx; i++)
        {
            if (!isUsableLeg(i)) continue;

            totalDistance += geometry[i].properties.distance;
        }

        let avgSTW = 0;
        let avgRPM = 0;
        let avgSpeed = 0;

        // Guard against division by zero when there are no usable legs.
        if (totalDistance <= 0) {
            return { rpm: 0, stw: 0, speed: 0 };
        }

        for (let i = startIdx; i < endIdx; i++)
        {
            if (!isUsableLeg(i)) continue;

            const legDistance = geometry[i].properties.distance;
            const legAvgRatio = legDistance / totalDistance;

            avgRPM += (geometry[i].properties.main_engine_rpm ?? 0) * legAvgRatio;
            avgSTW += (geometry[i].properties.speed_through_water ?? 0) * legAvgRatio;
            avgSpeed += (geometry[i].properties.speed_over_ground ?? 0) * legAvgRatio;
        }

        return {
            rpm: avgRPM,
            stw: avgSTW,
            speed: avgSpeed
        }
    }

export function getPilotPointsTimestamps(plan: VoyagePlan | null) {
    // If there is no plan, return -1 for both departure and arrival
    if(!plan) {
        return { departure: -1, arrival: -1 };
    }
    
    let departure = -1;
    let arrival = -1;

    if (plan.selectedRouteIndex === -1) {
        return { departure, arrival }
    }

    const route = plan.routes[plan.selectedRouteIndex];

    if (!route.route_info) {
        return { departure, arrival }
    }

    const ppIndeces = getPilotPointIndeces(route);
    if (ppIndeces.length === 0) {
        return { departure, arrival }
    }

    // If there is only one pilot point, it is assumed to be both departure and arrival pilot point (ie. there is nothing in between)
    if (ppIndeces.length > 1) {
        const depPilot = route.route_geometry[ppIndeces[0]];
        // Use ETA if present, otherwise use timestamps.
        departure = plan.departurePilotEtd > -1 ? plan.departurePilotEtd : new Date(depPilot.properties.timestamp).getTime();
    }

    const arrPilot = route.route_geometry[ppIndeces[ppIndeces.length-1]];
    // Use ETA if present, otherwise use timestamps.
    arrival = plan.arrivalPilotEta > -1 ? plan.arrivalPilotEta : new Date(arrPilot.properties.timestamp).getTime();

    return {
        departure,
        arrival
    };
}

/**
 * Same display-number rule as route editor: skip stoppage_end (second of stoppage pair), rank all other points 1,2,3...
 */
function getStoppageEndIndex(waypoints: VoyageRoutePointFeature[], idx: number): number {
    const wp = waypoints[idx];
    const nextIdx = idx + 1;
    if (wp?.properties?.is_stoppage_point !== true || wp?.properties?.stoppage_end === true) {
        return -1;
    }
    if (nextIdx >= waypoints.length) {
        return -1;
    }

    const nextWp = waypoints[nextIdx];
    return nextWp?.properties?.is_stoppage_point === true && nextWp?.properties?.stoppage_end === true
        ? nextIdx
        : -1;
}

function isHiddenStoppageEndRow(waypoints: VoyageRoutePointFeature[], idx: number): boolean {
    return idx > 0 && getStoppageEndIndex(waypoints, idx - 1) === idx;
}

export function getWaypointDisplayStartIndex(ecdisWPStartIndex?: number | null): number {
    return ecdisWPStartIndex === 0 ? -1 : 0;
}

export function getWaypointDisplayNumbers(waypoints: VoyageRoutePointFeature[], startFrom: number = 0): (number | null)[] {
    let rank = startFrom;
    const displayNumbers: (number | null)[] = [];

    for (let i = 0; i < waypoints.length; i++) {
        if (isHiddenStoppageEndRow(waypoints, i)) {
            displayNumbers[i] = null;
        } else {
            rank++;
            displayNumbers[i] = rank;
        }
    }

    return displayNumbers;
}

function getHardWaypointDisplayNumbers(waypoints: VoyageRoutePointFeature[], startFrom: number = 0): (number | null)[] {
    let rank = startFrom;
    const displayNumbers: (number | null)[] = [];

    for (let i = 0; i < waypoints.length; i++) {
        if (isHiddenStoppageEndRow(waypoints, i) || waypoints[i].properties.type !== "Hard") {
            displayNumbers[i] = null;
        } else {
            rank++;
            displayNumbers[i] = rank;
        }
    }

    return displayNumbers;
}

interface HardWaypointsInfoOptions {
    numberHardWaypointsOnly?: boolean;
    applyRouteEditorWaypointFilter?: boolean;
}

interface VisibleWaypointEntry {
    point: VoyageRoutePointFeature;
    geomIdx: number;
}

/**
 * Collect the waypoints a route displays, together with their geometry index and display number.
 * Shared by getHardWaypointsInfoFromRoutes and getPilotPointWaypointNumbers so both derive the
 * same numbering for a given start index and options.
 */
function getVisibleWaypointEntries(route: VoyageRoute, startHardWaypointsFromIndex: number, options: HardWaypointsInfoOptions) {
    const pointEntries: VisibleWaypointEntry[] = [];
    for (let i = 0; i < route.route_geometry.length; i++) {
        const f = route.route_geometry[i];
        if (f.geometry.type === "Point" && f.properties.type !== "Informational") {
            pointEntries.push({ point: f as VoyageRoutePointFeature, geomIdx: i });
        }
    }
    const visiblePointEntries = options.applyRouteEditorWaypointFilter
        ? pointEntries.filter(({ point }) => filterConditionNavigational(point))
        : pointEntries;
    const points = visiblePointEntries.map(({ point }) => point);

    const displayNumbers = options.numberHardWaypointsOnly
        ? getHardWaypointDisplayNumbers(points, startHardWaypointsFromIndex)
        // Match the route editor numbering exactly: every visible waypoint row gets a
        // display number, while the hidden stoppage-end row is skipped.
        : getWaypointDisplayNumbers(points, startHardWaypointsFromIndex);

    return { visiblePointEntries, points, displayNumbers };
}

export function getHardWaypointsInfoFromRoutes(routes: VoyageRoute[] | null, startHardWaypointsFromIndex: number = 1, options: HardWaypointsInfoOptions = {}): WaypointTimestamps[][] {
    if (!routes || routes.length === 0) return [];

    const hardWaypoints = [];
    for (const route of routes) {
        if (!route.route_info) {
            continue;
        }
        const ppIndeces = getPilotPointIndeces(route);
        const startIdx = ppIndeces.length ? ppIndeces[0] + 2 : 0;
        const endIdx = ppIndeces.length > 1 ? ppIndeces[ppIndeces.length - 1] - 2 : route.route_geometry.length - 1;

        const { visiblePointEntries, points, displayNumbers } = getVisibleWaypointEntries(route, startHardWaypointsFromIndex, options);

        const waypoints: WaypointTimestamps[] = [];
        for (let k = 0; k < points.length; k++) {
            const wpNumber = displayNumbers[k];
            if (wpNumber === null) continue;

            const point = points[k];
            const geomIdx = visiblePointEntries[k]?.geomIdx ?? 2 * k;
            const isPilot = ppIndeces.includes(geomIdx);

            // Always plot pilot points; otherwise only Hard waypoints within the pilot-to-pilot range
            if (!isPilot) {
                if (point.properties.type !== "Hard") continue;
                if (geomIdx < startIdx || geomIdx > endIdx) continue;
            }

            const timestamp = new Date(point.properties.timestamp!).getTime();
            // Only carry the ETA when the waypoint actually has one, so waypoints without it keep their previous shape
            const eta = point.properties.eta;
            waypoints.push(eta ? { wpNumber, timestamp, eta } : { wpNumber, timestamp });
        }
        hardWaypoints.push(waypoints);
    }
    return hardWaypoints;
}

/**
 * Get pilot point waypoint numbers from routes. Returns an array where each element
 * corresponds to a route and contains the waypoint numbers (display numbers) of pilot points.
 * Pass the same start index and options as the matching getHardWaypointsInfoFromRoutes call,
 * otherwise the returned numbers will not line up with the plotted waypoints.
 */
export function getPilotPointWaypointNumbers(routes: VoyageRoute[] | null, startHardWaypointsFromIndex: number = 1, options: HardWaypointsInfoOptions = {}): number[][] {
    if (!routes || routes.length === 0) return [];

    const pilotPointNumbers: number[][] = [];
    for (const route of routes) {
        if (!route.route_info) {
            pilotPointNumbers.push([]);
            continue;
        }

        const ppIndeces = getPilotPointIndeces(route);
        const { visiblePointEntries, displayNumbers } = getVisibleWaypointEntries(route, startHardWaypointsFromIndex, options);

        const pilotWpNumbers: number[] = [];
        for (const ppIdx of ppIndeces) {
            // Find which visible waypoint corresponds to this geometry index
            const pointIdx = visiblePointEntries.findIndex(({ geomIdx }) => geomIdx === ppIdx);
            if (pointIdx === -1) continue;

            const wpNumber = displayNumbers[pointIdx];
            if (wpNumber !== null && wpNumber !== undefined) {
                pilotWpNumbers.push(wpNumber);
            }
        }

        pilotPointNumbers.push(pilotWpNumbers);
    }
    return pilotPointNumbers;
}

export const filterConditionNavigational = (wp: VoyageRoutePointFeature) =>
    !(wp.properties.type === "Navigational" && wp.properties.isPassed && !wp.properties.userInserted);

// Waypoint properties the route editor lets the user change. Everything else on a waypoint is
// written by the optimiser, so it is not evidence of an edit.
const EDITABLE_WAYPOINT_PROPERTIES = [
    "type", "name", "comment", "eta", "etd", "pilotPoint", "isPassed", "openWaters",
    "is_stoppage_point", "stoppage_end", "stoppageReason", "userInserted",
    "speed_minimum", "speed_maximum", "min_depth", "userInput",
];

// Waypoints as the route editor works with them: the informational points it never lists are left
// out, so a saved route and the route it was edited from line up one to one.
function editableWaypoints(routeGeometry: RouteGeometry): VoyageRoutePointFeature[] {
    return <VoyageRoutePointFeature[]>(routeGeometry || []).filter(
        feature => feature?.geometry?.type === "Point" && feature.properties?.type !== "Informational");
}

// The editor writes '' for a cleared ETD and deletes the property outright when a waypoint stops
// being a stoppage point, so absent and empty have to compare equal.
function editableValueKey(value: any): any {
    if (value === undefined || value === null || value === "") {
        return "";
    }
    return typeof value === "object" ? JSON.stringify(value) : value;
}

/**
 * Whether a route re-saved from the route editor differs from the route it was opened on in
 * anything the user can edit - waypoints added, removed or moved, and the per-waypoint fields in
 * EDITABLE_WAYPOINT_PROPERTIES plus the leg types.
 *
 * A save normally always recalculates, because the editor rebuilds the route geometry from its
 * waypoints and the rebuilt legs carry none of the optimiser's results. This tells the save path
 * whether that recalculation is needed at all: a route that comes back untouched can be persisted
 * as it is.
 */
export function hasRouteEdits(oldGeometry: RouteGeometry, newGeometry: RouteGeometry): boolean {
    const oldWaypoints = editableWaypoints(oldGeometry);
    const newWaypoints = editableWaypoints(newGeometry);

    if (oldWaypoints.length !== newWaypoints.length) {
        return true;
    }

    for (let i = 0; i < oldWaypoints.length; i++) {
        const oldCoordinates = oldWaypoints[i].geometry.coordinates;
        const newCoordinates = newWaypoints[i].geometry.coordinates;
        if (oldCoordinates[0] !== newCoordinates[0] || oldCoordinates[1] !== newCoordinates[1]) {
            return true;
        }

        for (const property of EDITABLE_WAYPOINT_PROPERTIES) {
            if (editableValueKey((<any>oldWaypoints[i].properties)[property])
                !== editableValueKey((<any>newWaypoints[i].properties)[property])) {
                return true;
            }
        }
    }

    const legTypes = (routeGeometry: RouteGeometry) => (routeGeometry || [])
        .filter(feature => feature?.geometry?.type === "LineString")
        .map(feature => feature.properties?.leg_type ?? "")
        .join("|");

    return legTypes(oldGeometry) !== legTypes(newGeometry);
}

// Need to aggregate the times for all the data series, since echarts tooltips don't really work unless the data is uniform
    // - i.e. they all need to have the same x-axis entries
    // Using a set to avoid duplicating times
export function extractAllTimestamps(pilotPoints: PilotPointTimestamps, data: {[index in ChartNames]: TimeseriesData[]}, waypoints: WaypointTimestamps[][]) {
    let timesMap = new Map<number, TimestampData>();
    
    // pilot points timestamps
    if (pilotPoints.arrival > 0) {
        timesMap.set(pilotPoints.arrival, { timestamp: pilotPoints.arrival, isWp: false });
    }
    if (pilotPoints.departure > 0) {
        timesMap.set(pilotPoints.departure, { timestamp: pilotPoints.departure, isWp: false });
    }

    // performance and weather data
    for (const key in data) {
        for (const route of data[<ChartNames>key]) {
            for (const ts of route.timeseries.timestamps) {
                timesMap.set(ts, { timestamp: ts, isWp: false })
            }
        }
    }

    // waypoints timestamps
    for (const [idx, route]of waypoints.entries()) {
        for (const wp of route) {
            if (timesMap.has(wp.timestamp)) {
                timesMap.get(wp.timestamp)!.isWp = true;
                timesMap.get(wp.timestamp)!.wpNumber = wp.wpNumber;
                timesMap.get(wp.timestamp)!.routeIdx = idx;
                continue;
            }
            timesMap.set(wp.timestamp, { timestamp: wp.timestamp, isWp: true, routeIdx: idx, wpNumber: wp.wpNumber });
        }
    }

    const aggregatedTimes = Array.from(timesMap.values());
    aggregatedTimes.sort((a, b) => a.timestamp - b.timestamp);

    return aggregatedTimes;
}

/**
 * @name handleWeatherRouting
 * @description Show popup warning if weather is Beaufort 8 or route is crossing
 * weather routing lines. 
 */
export async function handleWeatherRouting(inputPlan: VoyagePlan, showFromPlan?: boolean ) {
    let plan = inputPlan;
    const scheduleId = plan.scheduleId;

    // Don't handle warning on both executing and planning plan.
    // Only use executing plan if there is one.
    const executingPlan = await VesselPlanningModel!.getExecutingPlan();
    if (executingPlan && executingPlan.scheduleId === scheduleId) {
        plan = executingPlan;
    }
// TODO:   need to verify this

    const currentSelectedRouteIndex = showFromPlan ? plan.selectedRouteIndex : plan.routes.findIndex(route => route.routeId === plan.currentSelectedRouteId)
    const routePassagePlanProperties = plan.routes[currentSelectedRouteIndex]?.passagePlan;
    
    if(!routePassagePlanProperties) return;
    
    // Add properties to plan, if they are not present
    if (!routePassagePlanProperties!.weatherRouting || !routePassagePlanProperties!.weatherRouting.hasOwnProperty("required")) {
        routePassagePlanProperties!.weatherRouting = {
            required: false,
            acknowledged: false,
            pilotPointAcknowledged: false
        };
    }

    const scheduleEntries = await VoyageSchedulesModel!.getScheduleFor(VoyageImoModel!.imo!);
    if (!scheduleEntries) return;

    const shouldCheckWarning = isActiveOrNextScheduleEntry(scheduleId, scheduleEntries);
    if (!shouldCheckWarning) return;

    const route = plan.routes[currentSelectedRouteIndex];
    if (!route.passagePlan) return;

    routePassagePlanProperties!.weatherRouting.required = isCrossingWeatherRoutingLines(route) || hasBeaufort8Weather(route);
    const weatherRouting = route.passagePlan?.weatherRouting;
    if (!weatherRouting!.required) return;

    // Only show the pop-up if the weatherRouting is required and not already acknowledged
    if (weatherRouting!.required && !weatherRouting!.acknowledged) {
        const popup = VoyageDialogPopupManager.createDialog({
            headerText: `Request WNI advice`,
            bodyText: `The route intended; requires WNI advice.
                    As per GLSM ID825 you are to reach out for 
                    WNI advice.`,
            noCloseButton: true,
            acceptButtonText: "Acknowledge"
        });

        await popup.getResponse();

        // update and set plan with user action
        route.passagePlan.weatherRouting!.acknowledged = true;
        VesselPlanningModel!.setPlan(scheduleId, plan, false);
    }
}

/**
 * @name handleWeatherRoutingPilotPoint
 * @description Show popup warning if current time is after departure pilot point
 * weather is Beaufort 8 or route is crossing weather routing lines.
 */
export async function handleWeatherRoutingPilotPoint(inputPlan: VoyagePlan) {
    let plan = inputPlan;
    const scheduleId = plan.scheduleId;

    // Don't handle warning on both executing and planning plan.
    // Only use executing plan if there is one.
    const executingPlan = await VesselPlanningModel!.getExecutingPlan();
    if (executingPlan && executingPlan.scheduleId === scheduleId) {
        plan = executingPlan;
    }

    const route = plan.routes[plan.selectedRouteIndex];
    if (!route.passagePlan) {
        return
    };

    const weatherRouting = route.passagePlan?.weatherRouting;
    if (!weatherRouting!.required || !weatherRouting!.acknowledged) { 
        return;
    }

    const now = new Date().getTime();
    let postPilotWarning = false;
    let departurePilotEtd;
    let pilotPoint = route.route_geometry.find(feature => feature.properties.pilotPoint);

    if (plan.departurePilotEtd < 0 && pilotPoint) {
        departurePilotEtd = new Date(pilotPoint.properties.timestamp).getTime();
    } else {
        departurePilotEtd = plan.departurePilotEtd;
    }

    if (!weatherRouting!.pilotPointAcknowledged && departurePilotEtd <= now) {
        postPilotWarning = true;
    }

    if (!postPilotWarning) return;

    const popup = VoyageDialogPopupManager.createDialog({
        headerText: `Request WNI advice`,
        bodyText:   `The route intended; requires WNI advice.
                    As per GLSM ID825 you are to reach out for 
                    WNI advice.`,
        noCloseButton: true,
        acceptButtonText: "Acknowledge"
    });

    await popup.getResponse();

    // update and set plan with user action
    route.passagePlan.weatherRouting!.acknowledged = true;
    if (postPilotWarning) {
        route.passagePlan.weatherRouting!.pilotPointAcknowledged = true;
    }

    VesselPlanningModel!.setPlan(scheduleId, plan, false);
}

/**
 * @name isActiveOrNextScheduleEntry
 * @description Check if scheduleId is active or next schedule entry.
 * Weather Routing should only be checked fo these two schedule entries.
 */
export function isActiveOrNextScheduleEntry(scheduleId: string, scheduleEntries: ScheduleEntry[]) {
    let activeEntryFound = false;

    for (const scheduleEntry of scheduleEntries) {
        if (activeEntryFound) {
            // next entry
            if (scheduleEntry.id === scheduleId) {
                return true;
            }
        }

        if (scheduleEntry.active) {
            activeEntryFound = true;
            if (scheduleEntry.id === scheduleId) {
                return true;
            }
        }
    }

    return false;
}

export function isCrossingWeatherRoutingLines(route: VoyageRoute): boolean {
    const borderLines: TurfFeature<turfLineString>[] = [];
    const features: Feature<Geometry>[] = [];

    //Atlantic Ocean would be a line From 63.00’00N ; 027.00’00W to 60.00’00S ; 027.00’00W
    borderLines.push(turfLine([[-27, 60], [-27, -60]]));
    //Pacific Ocean would be a line From 63.00’00N ; 175.00’00W to 60.00’00S ; 175.00’00W
    borderLines.push(turfLine([[-175, 63], [-175, -60]]));
    //Bay Of Biscay would be a line from 45.30’00N ; 001.10’00 W to 45.30’00N ; 12.00’00 W
    borderLines.push(turfLine([[-1.1666666666666667, 45.5], [-12, 45.5]]));
    //Saracossa Sea would be a line from 17.30’00N ; 61.40’00W to 17.30’00N ; 58.00’00 W
    borderLines.push(turfLine([[-61.666666666666664, 17.5], [-58, 17.5]]));
    //Africa West Coast would be a line from 10.00’00S ; 003.00’00E to 10.00’00S ; 10.00’00 W
    borderLines.push(turfLine([[3, -10], [-10, -10]]));
    //New Zealand to Far East would be 29.30’00S ; 166.00’00E to 29.30’00S ; 175.00’00E
    borderLines.push(turfLine([[166, -29.5], [175, 29.5]]));

    route.route_geometry.filter(f => f.geometry.type === "LineString").forEach(f => {
        const lineString = new Feature({
            geometry: new LineString(f.geometry.coordinates)
        });
        
        if (f.properties.leg_type) {
            lineString.setProperties({"leg_type": f.properties.leg_type});
        }

        features.push(...CreateDateLineAndGreatCircleFeatures(lineString));
    })

    const routeLines = features.filter(f => f.getGeometry()?.getType() === "LineString").map(l => turfLine((<LineString>l.getGeometry()).getCoordinates()));

    let isCrossing = false;

    borderLines!.forEach(borderLine => {
        routeLines.forEach(line => {
            const intersections = lineIntersect(line, borderLine);
            if (intersections.features.length > 0) {
                isCrossing = true;
                return;
            }
        });
        if (isCrossing) return;
    });

    return isCrossing;
}

export function hasBeaufort8Weather(route: VoyageRoute): boolean {
    let hasB8Weather = false;
    route.route_geometry.filter(f => f.geometry.type === "LineString").forEach(f => {
        if (f.properties.weather && f.properties.weather.Wind10M.max.value > 17) {
            hasB8Weather = true;
            return;
        }
        if (hasB8Weather) return;
    });
    
    return hasB8Weather;
}

/**
 * @name createTimestampFillerSeries
 * @description Add prefill values to the passage plan
 * @param {VoyagePassagePlan} PassagePlan
 * 
 * @returns {void} void
 */
export function addPrefillValuesToPassagePlan(passagePlan: RoutePassagePlanProperties | VoyagePlan["passagePlan"]) {
    passagePlan.booksAndTables = passagePlan.booksAndTables || `ENP 100 - The Mariner's Handbook \nENP 136 - Ocean passages of World \nICS Bridge Procedures Guide \nGuidelines For Navigators \nIMO STCW Code \nGLSM \nSquat Table \nCOLREG \nRelevant local regulations\n`;
    passagePlan.tideTables = passagePlan.tideTables || "ATT – Admiralty Total Tide";
    passagePlan.listOfRadioSignals = passagePlan.listOfRadioSignals || "ADRS – Admiralty Digital Radio Signals Volume 1, 2, 3, 4, 5, 6.";
    passagePlan.listOfLights = passagePlan.listOfLights || "ADLL – Admiralty Digital List of Lights";
    passagePlan.publicationsNotes = passagePlan.publicationsNotes|| `All Up to Date \nEnvironmental protection: While preparing this passage plan, MARPOL and SOLAS requirements, as well as applicable and readily available local requirements ,i.e Port Memo, have been taken into account. In addition, all relevant company procedures are being followed.`;
    passagePlan.equipmentStatus.operationalCondition = passagePlan.equipmentStatus.operationalCondition || "All in good working condition.",
    passagePlan!.equipmentStatus.failures = passagePlan.equipmentStatus.failures || "Nil";
    passagePlan.mastersOrders = passagePlan.mastersOrders || "As per Master´s Standing order and Daily orders";
    passagePlan.chartsAndLightsNotes = passagePlan.chartsAndLightsNotes || `All Up to Date. \nAll charts which are not referring to the WGS-84 datum should be listed below.`;
    passagePlan.weatherInfo.info = `SPOS \nEGC \nNAVTEX \nStarConnect Vessel Voyage \nWNI`;
}

export const _checkRouteAlignment = async (selectedRoute: any, plan: VoyagePlan) => {
    try{
        const starRoutesInPlan  = plan?.routes.filter(route => route.routeCreatedBy === 'system');
        let isAlignedFlag = false;
        for (const route of starRoutesInPlan) {
            const isAligned = await VesselPlanningModel!.isAlignedRoute(
                selectedRoute, 
                route.bestPracticeRouteId || ''
            );
            if (isAligned) {
                isAlignedFlag = true;
                break;  // No need to check further if one is aligned
            }
        }
        return isAlignedFlag;
    }
    catch(err){
        console.error(err);
        return false;
    }
   
}

export function validateExtension(extension: string, validExtensions: string[] = ECDIS_FORMATS): boolean {
    if (!extension) return false;
    return validExtensions.includes(extension);
}

export const getRemainingDistanceFromCurrentWP = async (route: VoyageRoute) =>{

    const positionData = VesselPositionModel!.position;

    if (!positionData || !positionData.latitude || !positionData.longitude) {
        console.log("Vessel position not available");
        return '-'
    }
    const position : Coordinate = [positionData.longitude, positionData.latitude]

    if (!route || !route.route_geometry || !route.route_geometry.length) {
        console.log("Route geometry not available on view route");
        return '-'
    }
    const nextWayPointIndex = await getNextWaypointIndex(route.route_geometry);

    if (nextWayPointIndex === -1) {
        console.log("No next waypoint found in route");
        return '-';
    }

    const defaultResult = '-';
    const KmToNM = 1 / 1.852;

    if (nextWayPointIndex == null || nextWayPointIndex === -1) {
        console.log("No next waypoint found in route");
        return defaultResult;
    }

    const nextWaypoint = route.route_geometry[nextWayPointIndex];
    const nextWaypointCoords = nextWaypoint?.geometry.type === 'Point'
    ? nextWaypoint.geometry.coordinates as number[]
    : null;

    const lastWaypointIndex = route.route_geometry.length - 1;
    if (!nextWaypointCoords) {
        console.log("Next waypoint coordinates not found");
        return defaultResult;
    }

    const dists = CoordinatesUtils.calcDistance(position, nextWaypointCoords);
    const incomingLeg = route.route_geometry[nextWayPointIndex - 1];
    const distanceToNextWP = incomingLeg?.properties.leg_type === "GreatCircle" ? dists.gc : dists.rl;
    const distanceFromNextWpToLastWp = getRouteDistance(route, nextWayPointIndex, lastWaypointIndex) * KmToNM;
    const remainingDistance = distanceToNextWP + distanceFromNextWpToLastWp;

    return remainingDistance.toFixed(1) + ' nm';
}

export const getTotalDistance = (route: VoyageRoute) => {
    //returns the total distance of whole voyage
    const KmToNM = 1 / 1.852;
    const firstWaypointIndex = 0;
    const lastWaypointIndex = route.route_geometry.length - 1;
    const totalDistance = getRouteDistance(route, firstWaypointIndex, lastWaypointIndex) * KmToNM;

    return totalDistance.toFixed(1) + ' nm';
}

// alignment is true in case selectedRoute is within 10nm and 50nm tolerance for RL and GC, respectively
export const _checkRouteAlignmentToStarRoutes = async (selectedRoute: VoyageRoute, departureTerminalCode: string, arrivalTerminalCode: string, scheduleId: string) : Promise<boolean> => {
    try{
        let starRoutes = await VesselPlansService.getOptimizedBPRsByPortPair(departureTerminalCode, arrivalTerminalCode, scheduleId);
        if ( !starRoutes  || !Array.isArray(starRoutes) || starRoutes.length === 0 ) {
            console.log("no star route found for the port pair, skipping alignment check");
            return false;
        }

        let isAlignedToStarRoute = false;
        for (const route of starRoutes) {
            const isRouteAligned = await VesselPlanningModel!.isAlignedRoute(
                selectedRoute, 
                route.best_practice_route_id
            );
            if (isRouteAligned) {
                isAlignedToStarRoute = true;
                break;  // No need to check further if one is aligned
            }
        }
        return isAlignedToStarRoute;
    }
    catch(err){
        console.error(err);
        return false;
    }
}

// Returns human-readable descriptions of the contiguous route segments whose deviation from the
// top-1 star route exceeds the allowed threshold, from the persisted bpr_deviation_exceeds flags.
export function getStarRouteDeviationSegments(routeGeometry: RouteGeometry): string[] {
    const exceedingWaypoints = new Set<number>();
    let wpNumber = 0;

    for (const feature of routeGeometry) {
        if (feature.geometry.type === 'Point') {
            wpNumber++;
            if ((feature.properties as any)?.bpr_deviation_exceeds === true) {
                exceedingWaypoints.add(wpNumber);
            }
        } else if (feature.geometry.type === 'LineString') {
            if ((feature.properties as any)?.bpr_deviation_exceeds === true) {
                exceedingWaypoints.add(wpNumber);
                exceedingWaypoints.add(wpNumber + 1);
            }
        }
    }

    const ranges: string[] = [];
    const sortedWaypoints = [...exceedingWaypoints].sort((left, right) => left - right);
    let rangeStart: number | null = null;
    let rangeEnd = 0;

    const flush = () => {
        if (rangeStart !== null) {
            ranges.push(rangeStart === rangeEnd ? `Waypoint ${rangeStart}` : `Waypoints ${rangeStart}-${rangeEnd}`);
        }
    };

    for (const waypoint of sortedWaypoints) {
        if (rangeStart === null) {
            rangeStart = rangeEnd = waypoint;
        } else if (waypoint === rangeEnd + 1) {
            rangeEnd = waypoint;
        } else {
            flush();
            rangeStart = rangeEnd = waypoint;
        }
    }
    flush();
    return ranges;
}

export function getMostFuelEfficientStarRoute(routes: VoyageRoute[]): VoyageRoute | undefined {
    return routes
        .filter(route => route.routeCreatedBy === 'system' && route.uiType !== 'OOR')
        .sort((left, right) => getRouteFuel(left) - getRouteFuel(right))[0];
}

function getRouteFuel(route: VoyageRoute): number {
    const totals = route.route_info?.totals;
    const fuel = (totals?.non_eca?.fuel ?? Number.POSITIVE_INFINITY)
        + (totals?.eca?.fuel ?? 0);
    return Number.isFinite(fuel) ? fuel : Number.POSITIVE_INFINITY;
}
